/**
 * CategorySidebar Component
 * Displays 6 category buttons in the sidebar instead of 30+ individual menu items
 */

import React from 'react';
import { menuCategories } from '../../config/menuCategories';
import { User, Settings, HelpCircle, LogOut, ChevronRight, ChevronLeft, Home } from 'lucide-react';

const CategorySidebar = ({
    user,
    page,
    navigate,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    handleLogoutAndNavigate,
    aiModel,
    setAiModel,
    isMobile = false
}) => {
    // Check if current page belongs to a category
    const getActiveCategory = () => {
        for (const category of menuCategories) {
            if (category.items.some(item => item.targetPage === page)) {
                return category.id;
            }
        }
        // Check if we're on the category menu page
        if (page?.startsWith('category-')) {
            return page.replace('category-', '');
        }
        return null;
    };

    const activeCategory = getActiveCategory();

    const handleCategoryClick = (categoryId) => {
        navigate(`category-${categoryId}`);
    };

    return (
        <>
            {/* Sidebar Header */}
            <div className="flex items-center justify-between mb-6">
                {!isSidebarCollapsed && (
                    <div className="font-bold text-2xl text-white px-2">
                        CikGu by Al-Fazza
                    </div>
                )}
                {/* Hide collapse button on mobile - mobile uses hamburger menu */}
                {!isMobile && (
                    <button
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        className="p-2 rounded-lg text-purple-100 hover:bg-purple-700 hover:text-white transition-colors"
                        title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>
                )}
            </div>

            {/* Category Navigation */}
            <nav className="flex-grow space-y-2">
                {menuCategories.map((category) => {
                    const Icon = category.icon;
                    const isActive = activeCategory === category.id;

                    return (
                        <div key={category.id} className="relative group">
                            <button
                                onClick={() => handleCategoryClick(category.id)}
                                className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                                    ? 'bg-purple-600 text-white shadow-lg'
                                    : 'text-purple-100 hover:bg-white/10 hover:text-white'
                                    } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                title={isSidebarCollapsed ? category.label : undefined}
                            >
                                <div className={`p-2 rounded-lg ${isActive ? 'bg-purple-600' : ''}`}>
                                    <Icon size={20} />
                                </div>
                                {!isSidebarCollapsed && (
                                    <div className="ml-3 text-left">
                                        <span className="font-medium text-sm">{category.label}</span>
                                        <p className="text-xs text-purple-200 opacity-75 truncate max-w-[140px]">
                                            {category.items.length} menu
                                        </p>
                                    </div>
                                )}
                            </button>

                            {/* Tooltip for collapsed mode */}
                            {isSidebarCollapsed && (
                                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                                    <div className="font-medium">{category.label}</div>
                                    <div className="text-xs text-gray-400">{category.items.length} menu</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* Bottom Section */}
            <div className="mt-auto space-y-2">

                {/* Home / Landing Page */}
                <div className="relative group">
                    <button
                        onClick={() => navigate('landing')}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${page === 'landing'
                            ? 'bg-purple-600 text-white'
                            : 'text-purple-100 hover:bg-white/10 hover:text-white'
                            } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? 'Beranda Utama' : undefined}
                    >
                        <Home size={20} />
                        {!isSidebarCollapsed && <span className="ml-3 font-medium text-sm">Beranda Utama</span>}
                    </button>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                            Beranda Utama
                        </div>
                    )}
                </div>

                {/* Profile */}
                <div className="relative group">
                    <button
                        onClick={() => navigate('guru_profil')}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${page === 'guru_profil'
                            ? 'bg-purple-600 text-white'
                            : 'text-purple-100 hover:bg-white/10 hover:text-white'
                            } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? 'Profil Guru' : undefined}
                    >
                        <User size={20} />
                        {!isSidebarCollapsed && <span className="ml-3 font-medium text-sm">Profil Guru</span>}
                    </button>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                            Profil Guru
                        </div>
                    )}
                </div>

                {/* Settings */}
                <div className="relative group">
                    <button
                        onClick={() => navigate('settings')}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${page === 'settings'
                            ? 'bg-purple-600 text-white'
                            : 'text-purple-100 hover:bg-white/10 hover:text-white'
                            } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? 'Pengaturan' : undefined}
                    >
                        <Settings size={20} />
                        {!isSidebarCollapsed && <span className="ml-3 font-medium text-sm">Pengaturan</span>}
                    </button>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                            Pengaturan
                        </div>
                    )}
                </div>

                {/* Help */}
                <div className="relative group">
                    <button
                        onClick={() => navigate('bantuan')}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${page === 'bantuan'
                            ? 'bg-purple-600 text-white'
                            : 'text-purple-100 hover:bg-white/10 hover:text-white'
                            } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? 'Pusat Bantuan' : undefined}
                    >
                        <HelpCircle size={20} />
                        {!isSidebarCollapsed && <span className="ml-3 font-medium text-sm">Pusat Bantuan</span>}
                    </button>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                            Pusat Bantuan
                        </div>
                    )}
                </div>

                {/* Logout */}
                <div className="relative group">
                    <button
                        onClick={handleLogoutAndNavigate}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 text-purple-100 hover:bg-red-600 hover:text-white ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? 'Keluar' : undefined}
                    >
                        <LogOut size={20} />
                        {!isSidebarCollapsed && <span className="ml-3 font-medium text-sm">Keluar</span>}
                    </button>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                            Keluar
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CategorySidebar;
